// src/renderer/components/dialogs/UnsavedChangesDialog.tsx

import React, { useRef, useEffect } from 'react';
import { Dialog } from '../common/Dialog';
import { Button } from '../common/Button';
import { AlertTriangle, Save } from 'lucide-react';

export interface UnsavedChangesDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback when the dialog is closed (via overlay or escape) */
  onClose: () => void;
  /** Project name to display */
  projectName: string;
  /** Callback when "Don't Save" button is clicked */
  onDontSave: () => void;
  /** Callback when "Cancel" button is clicked */
  onCancel: () => void;
  /** Callback when "Save" button is clicked */
  onSave: () => void;
  /** Whether a save operation is in progress */
  isSaving?: boolean;
}

// Generate unique IDs for accessibility
const DIALOG_TITLE_ID = 'unsaved-changes-dialog-title';
const DIALOG_DESCRIPTION_ID = 'unsaved-changes-dialog-description';

export const UnsavedChangesDialog: React.FC<UnsavedChangesDialogProps> = ({
  isOpen,
  onClose,
  projectName,
  onDontSave,
  onCancel,
  onSave,
  isSaving = false,
}) => {
  // Ref for the Save button to focus when dialog opens
  const saveButtonRef = useRef<HTMLButtonElement>(null);

  // Focus the Save button when dialog opens
  useEffect(() => {
    if (isOpen && saveButtonRef.current) {
      // Small delay to ensure the dialog is fully rendered
      const timeoutId = setTimeout(() => {
        saveButtonRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      closeOnOverlayClick={!isSaving}
      closeOnEscape={!isSaving}
      aria-labelledby={DIALOG_TITLE_ID}
      aria-describedby={DIALOG_DESCRIPTION_ID}
    >
      <div className="p-6" data-testid="unsaved-changes-dialog">
        {/* Header with icon */}
        <div className="flex items-start gap-4 mb-4">
          <div className="p-3 rounded-full bg-yellow-500/20" data-testid="warning-icon-container">
            <AlertTriangle
              className="w-6 h-6 text-yellow-500"
              data-testid="warning-icon"
              aria-hidden="true"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id={DIALOG_TITLE_ID}
              className="text-lg font-semibold text-zinc-100"
              data-testid="dialog-title"
            >
              Unsaved Changes
            </h2>
            <p className="text-sm text-zinc-400 mt-0.5" data-testid="project-name">
              &ldquo;{projectName}&rdquo;
            </p>
          </div>
        </div>

        {/* Message */}
        <p
          id={DIALOG_DESCRIPTION_ID}
          className="text-zinc-300 mb-6"
          data-testid="dialog-message"
        >
          Do you want to save changes to this project before closing? Your changes will be lost if you don&apos;t save them.
        </p>

        {/* Actions */}
        <div className="flex justify-end gap-3" role="group" aria-label="Dialog actions">
          <Button
            variant="ghost"
            onClick={onDontSave}
            disabled={isSaving}
            data-testid="dont-save-button"
            aria-describedby={DIALOG_DESCRIPTION_ID}
          >
            Don&apos;t Save
          </Button>
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isSaving}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            ref={saveButtonRef}
            variant="primary"
            icon={<Save className="w-4 h-4" aria-hidden="true" />}
            onClick={onSave}
            loading={isSaving}
            disabled={isSaving}
            data-testid="save-button"
            aria-describedby={DIALOG_DESCRIPTION_ID}
          >
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default UnsavedChangesDialog;
